import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { studentService, countryService, stateService, districtService, universityService, collegeService } from '../services/api';
import './Register.css';

function StudentRegister() {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    address: '',
    location: '',
    universityRegNo: '',
    countryId: '',
    stateId: '',
    districtId: '',
    universityId: '',
    collegeId: ''
  });
  const [marksheetFile, setMarksheetFile] = useState(null);

  useEffect(() => {
    countryService.getCountries().then(setCountries).catch(() => setCountries([]));
    universityService.getUniversities().then(setUniversities).catch(() => setUniversities([]));
    collegeService.getColleges().then(setColleges).catch(() => setColleges([]));
  }, []);

  useEffect(() => {
    if (formData.countryId) {
      stateService.getStates().then((d) => setStates(d.filter(s => s.CountryId === parseInt(formData.countryId)))).catch(() => setStates([]));
    } else setStates([]);
  }, [formData.countryId]);

  useEffect(() => {
    if (formData.stateId) {
      districtService.getDistricts().then((d) => setDistricts(d.filter(x => x.StateId === parseInt(formData.stateId)))).catch(() => setDistricts([]));
    } else setDistricts([]);
  }, [formData.stateId]);

  useEffect(() => {
    if (!formData.universityId && formData.collegeId) {
      setFormData((prev) => ({ ...prev, collegeId: '' }));
      return;
    }

    if (formData.universityId && formData.collegeId) {
      const selectedUniversityId = parseInt(formData.universityId);
      const selectedCollegeId = parseInt(formData.collegeId);
      const isCollegeUnderUniversity = colleges.some(
        (college) => college.Id === selectedCollegeId && college.UniversityId === selectedUniversityId
      );

      if (!isCollegeUnderUniversity) {
        setFormData((prev) => ({ ...prev, collegeId: '' }));
      }
    }
  }, [formData.universityId, formData.collegeId, colleges]);

  const filteredColleges = formData.universityId
    ? colleges.filter((college) => college.UniversityId === parseInt(formData.universityId))
    : [];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!formData.universityRegNo?.trim() && !marksheetFile) {
      setError('Please enter University Register No or upload Marksheet PDF');
      return;
    }
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append('Name', formData.name.trim());
      fd.append('Email', formData.email.trim());
      fd.append('MobileNo', formData.phone.trim());
      fd.append('Password', formData.password);
      fd.append('Address', formData.address.trim());
      fd.append('Location', formData.location.trim());
      fd.append('UniversityRegNo', formData.universityRegNo.trim());
      fd.append('CountryId', formData.countryId || 0);
      fd.append('StateId', formData.stateId || 0);
      fd.append('DistrictId', formData.districtId || 0);
      fd.append('UniversityId', formData.universityId || 0);
      fd.append('CollegeId', formData.collegeId || 0);
      if (marksheetFile) fd.append('Marksheet', marksheetFile);
      await studentService.saveStudentWithMarksheet(fd);
      alert('Registration submitted. Your college coordinator will review and approve. You will be notified once approved.');
      navigate('/login');
    } catch (err) {
      setError(err.message || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-page">
      <div className="register-container">
        <div className="register-header">
          <h1>Student Registration</h1>
          <p>Create your account. Coordinator approval required before you can apply for jobs.</p>
        </div>

        <form className="register-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone *</label>
              <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">Password *</label>
              <input type="password" id="password" name="password" value={formData.password} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password *</label>
              <input type="password" id="confirmPassword" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="countryId">Country *</label>
            <select id="countryId" name="countryId" value={formData.countryId} onChange={handleChange} required>
              <option value="">Select Country</option>
              {countries.map((c) => <option key={c.Id} value={c.Id}>{c.Name}</option>)}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="stateId">State *</label>
              <select id="stateId" name="stateId" value={formData.stateId} onChange={handleChange} required disabled={!formData.countryId}>
                <option value="">Select State</option>
                {states.map((s) => <option key={s.Id} value={s.Id}>{s.Name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="districtId">District *</label>
              <select id="districtId" name="districtId" value={formData.districtId} onChange={handleChange} required disabled={!formData.stateId}>
                <option value="">Select District</option>
                {districts.map((d) => <option key={d.Id} value={d.Id}>{d.Name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="universityId">University *</label>
              <select id="universityId" name="universityId" value={formData.universityId} onChange={handleChange} required>
                <option value="">Select University</option>
                {universities.map((u) => <option key={u.Id} value={u.Id}>{u.Name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="collegeId">College *</label>
              <select id="collegeId" name="collegeId" value={formData.collegeId} onChange={handleChange} required disabled={!formData.universityId}>
                <option value="">Select College</option>
                {filteredColleges.map((c) => <option key={c.Id} value={c.Id}>{c.Name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="address">Address</label>
            <input type="text" id="address" name="address" value={formData.address} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label htmlFor="location">Location / City</label>
            <input type="text" id="location" name="location" value={formData.location} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label htmlFor="universityRegNo">University Register No</label>
            <input
              type="text"
              id="universityRegNo"
              name="universityRegNo"
              value={formData.universityRegNo}
              onChange={handleChange}
              placeholder="Enter your university registration number"
            />
            <small style={{ color: '#666' }}>Provide at least one: University Reg No or Marksheet PDF</small>
          </div>
          <div className="form-group">
            <label htmlFor="marksheet">Upload Marksheet (PDF)</label>
            <input
              type="file"
              id="marksheet"
              accept=".pdf,application/pdf"
              onChange={(e) => setMarksheetFile(e.target.files[0] || null)}
            />
          </div>
          {error && (
            <div className="error-message" style={{ color: '#dc3545', padding: '0.75rem', backgroundColor: '#f8d7da', borderRadius: '5px', marginBottom: '1rem' }}>{error}</div>
          )}
          <div className="form-checkbox">
            <label><input type="checkbox" required /><span>I agree to the Terms and Conditions *</span></label>
          </div>
          <button type="submit" className="register-btn" disabled={loading}>{loading ? 'Submitting...' : 'Register'}</button>
          <div className="login-link">
            <p>Already have an account? <Link to="/login">Login here</Link></p>
          </div>
        </form>
      </div>
    </div>
  );
}

export default StudentRegister;
